// stores/AppStore.ts (Versão corrigida - sem useI18n dentro da store)
import { defineStore } from "pinia";
import versionRepo from "../repository/version";
type AppConfigurations = {
  language: "pt" | "en" | "es";
};

// Versão do app será definida dinamicamente
export const useAppStore = defineStore("appStore", () => {
  const config = useRuntimeConfig();

  /**
   * URL utilizada para as requisições
   */
  const url =
    process.env[`NODE_ENV`] !== "development"
      ? `${location.origin}/api`
      : config.public.apiBase;

  // ❌ REMOVIDO: const i18n = useI18n(); (causava o erro)
  // ❌ REMOVIDO: const isOnline = useOnline(); (pode causar problemas similares)

  // Estados locais que não dependem de composables
  const isOnline = ref(true); // Será gerenciado via método próprio
  const isFullScreen = ref<boolean>(false);
  const validation = ref<boolean>(false);
  const showModalLogin = ref<boolean>(false);
  const showModalVariavel = ref<boolean>(false);
  const isOpen = ref(false);
  const configurations = reactive<AppConfigurations>({
    language: "pt",
  });

  // Versão do app (inicializa a partir do runtimeConfig e pode ser atualizada pela API)
  const version = ref<string>(
    (typeof useRuntimeConfig === "function"
      ? (useRuntimeConfig().public as any)?.appVersion
      : undefined) || "0.0.0",
  );

  function setVersion(v: string) {
    if (typeof v === "string" && v.trim().length > 0) {
      version.value = v.trim();
    }
  }

  // Gerenciamento de conectividade manual (substitui useOnline)
  const updateOnlineStatus = () => {
    if (typeof window !== "undefined") {
      isOnline.value = navigator.onLine;
    }
  };

  // Escuta mudanças de conectividade apenas no cliente
  if (typeof window !== "undefined") {
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);
    updateOnlineStatus(); // Define estado inicial
    // Atualiza a versão do app a partir do endpoint remoto, sem bloquear a inicialização
    (async () => {
      try {
        const resp: any = await versionRepo.getVersion();
        let ver = "0.0.0";
        if (typeof resp === "string") {
          ver = resp;
        } else if (Array.isArray(resp) && resp.length > 0) {
          const first = resp[0];
          if (typeof first === "string") ver = first;
          else if (first && (first.version || first.Versao || first.versao))
            ver = first.version ?? first.Versao ?? first.versao;
        } else if (resp && (resp.version || resp.Versao || resp.versao)) {
          ver = resp.version ?? resp.Versao ?? resp.versao;
        }

        if (ver && ver !== "0.0.0") {
          setVersion(String(ver));
        }
      } catch (error) {
        // Não interrompe a aplicação se a chamada falhar
        // eslint-disable-next-line no-console
        console.warn("Erro ao obter versão remota:", error);
      }
    })();
  }

  type TemaSite = {
    cor: string;
  };

  // Tema do site com acesso seguro ao localStorage
  const temaSite = computed<TemaSite>({
    get: () => {
      if (typeof window === "undefined") return { cor: "azul" };

      try {
        const storedValue = localStorage.getItem("temaSite");
        return storedValue ? { cor: storedValue } : { cor: "azul" };
      } catch {
        return { cor: "azul" };
      }
    },
    set: (value: TemaSite) => {
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("temaSite", value.cor);
        } catch (error) {
          console.warn("[AppStore] Erro ao salvar tema:", error);
        }
      }
    },
  });

  // Método para definir idioma (será chamado pelos componentes)
  function setLanguage(language: "pt" | "en" | "es") {
    configurations.language = language;

    // O componente que chama este método deve ter acesso ao useI18n
    // e fazer: const { setLocale } = useI18n(); setLocale(language);

    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("app-language", language);
        location.reload();
      } catch (error) {
        console.warn("[AppStore] Erro ao salvar idioma:", error);
      }
    }
  }

  // Método para retornar à página pai
  // Agora recebe as traduções como parâmetros em vez de usar i18n diretamente
  async function returnToParentPage(
    messageToParent?: string,
    translations?: {
      title: string;
      text: string;
      confirmButton: string;
      cancelButton: string;
    },
  ) {
    // Se não receber traduções, usa valores padrão
    const defaultTranslations = {
      title: "Confirmar",
      text: "Deseja retornar à página anterior?",
      confirmButton: "Confirmar",
      cancelButton: "Cancelar",
    };

    const trans = translations || defaultTranslations;

    // Aqui você precisa importar sua função de alerta
    // Como não vi a implementação no código, vou deixar um placeholder
    const result = await showConfirmDialog({
      text: trans.title + trans.text,
      confirmButtonText: trans.confirmButton,
      cancelButtonText: trans.cancelButton,
    });

    if (result.isDismissed) {
      return;
    }

    if (typeof window !== "undefined" && window.opener) {
      window.opener.postMessage(messageToParent ?? "Return to parent", "*");
      window.close();
    }
  }

  // Método para alternar tela cheia
  function toggleFullScreen() {
    isFullScreen.value = !isFullScreen.value;
  }

  // Watch para tela cheia
  watch(isFullScreen, async (newFullScreenValue) => {
    if (typeof window === "undefined") return;

    if (!newFullScreenValue && document.fullscreenElement != null) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error("Erro ao sair do modo de tela cheia:", error);
      }
    } else if (newFullScreenValue && document.fullscreenElement == null) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (error) {
        console.error("Erro ao entrar no modo de tela cheia:", error);
      }
    }
  });

  // Watch para conectividade (será acionado por componentes)
  const onlineStatusCallbacks = new Set<(isOnline: boolean) => void>();

  const addOnlineStatusListener = (callback: (isOnline: boolean) => void) => {
    onlineStatusCallbacks.add(callback);

    // Remove listener quando não precisar mais
    return () => onlineStatusCallbacks.delete(callback);
  };

  watch(isOnline, (newIsOnlineValue) => {
    // Notifica todos os listeners registrados
    onlineStatusCallbacks.forEach((callback) => callback(newIsOnlineValue));
  });

  // Cleanup dos event listeners
  const cleanup = () => {
    if (typeof window !== "undefined") {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    }
  };

  return {
    // Estados
    configurations: readonly(configurations),
    isOnline: readonly(isOnline),
    showModalLogin,
    validation,
    temaSite,
    originUrl: url,
    isFullScreen: readonly(isFullScreen),
    url,
    version,
    setVersion,
    // Métodos
    setLanguage,
    returnToParentPage,
    toggleFullScreen,
    updateOnlineStatus,
    addOnlineStatusListener,
    cleanup,
    showModalVariavel,
    isOpen,
  };
});

// Função auxiliar para diálogos de confirmação
// Você deve substituir pela sua implementação atual
async function showConfirmDialog(options: {
  text: string;
  confirmButtonText: string;
  cancelButtonText: string;
}): Promise<{ isDismissed: boolean }> {
  // Implementação placeholder - substitua pela sua função real
  const result = confirm(options.text);
  return { isDismissed: !result };
}
